import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, CheckCircle2, ShieldCheck, Loader2, HeartHandshake } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { usePitaLogo, PitaShareCardBlock } from '../components/PitaCTA';

const ROLES = [
  { value: 'resident', label: 'Resident' },
  { value: 'community_group', label: 'Community group' },
  { value: 'councillor', label: 'Councillor' },
  { value: 'councillor_candidate', label: 'Councillor candidate' },
  { value: 'mayor', label: 'Mayor' },
  { value: 'mayor_candidate', label: 'Mayor candidate' },
];

const WARDS = [
  { value: 'not_provided', label: 'Prefer not to say' },
  { value: '1', label: 'Ward 1' }, { value: '2', label: 'Ward 2' },
  { value: '3', label: 'Ward 3' }, { value: '4', label: 'Ward 4' },
  { value: '5', label: 'Ward 5' }, { value: '6', label: 'Ward 6' },
  { value: '7', label: 'Ward 7' }, { value: '8', label: 'Ward 8' },
  { value: 'citywide', label: 'Citywide / Mayor race' },
];

const DISPLAY_CHOICES = [
  { value: 'first_name', label: 'First name only' },
  { value: 'full_name', label: 'Full name' },
  { value: 'pseudonym', label: 'A pseudonym I choose' },
  { value: 'anonymous', label: 'Anonymous' },
];

const EMPTY = {
  name: '', email: '', role: 'resident', organization: '', ward: 'not_provided',
  display_choice: 'first_name', pseudonym: '', statement: '',
  consent_public: false, is_adult: false, website: '',
};

export default function SupportPita() {
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pitaLogo = usePitaLogo('support');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailOk = /.+@.+\..+/.test(form.email);
  const isOfficial = ['councillor', 'councillor_candidate', 'mayor', 'mayor_candidate'].includes(form.role);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Please enter your name'); return; }
    if (!emailOk) { toast.error('Please enter a valid email address'); return; }
    if (form.display_choice === 'pseudonym' && !form.pseudonym.trim()) { toast.error('Please enter the pseudonym you want displayed'); return; }
    if (!form.is_adult) { toast.error('Please confirm you are 18 or older'); return; }
    setBusy(true);
    try {
      await api.post('/pita/support', form);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="support-pita-success">
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
        <h1 className="font-serif font-black text-[#1a2744] text-2xl sm:text-3xl mt-4">Thank you for supporting PITA</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Your submission is private and will be reviewed personally. Nothing appears publicly unless you asked to be
          listed and the campaign approves it. Your email is never shown publicly.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="h-12 px-5 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press justify-center" data-testid="support-pita-success-supporters-btn">
            <Link to="/pita-supporters">View Supporters by Ward</Link>
          </Button>
          <Button asChild variant="outline" className="h-12 px-5 border-[#1a2744] text-[#1a2744] font-bold btn-press justify-center" data-testid="support-pita-success-tracker-btn">
            <Link to="/issue-tracker">See the Ward 8 Tracker</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-pita-page">
      <PageHeader
        kicker="PUBLIC ISSUE TRACKER APP (PITA)"
        title="Support PITA"
        subtitle="Add your name to the residents, community groups, and candidates who believe Kawartha Lakes deserves better reporting, mapping, and follow-up tools."
        logo={pitaLogo?.url}
        logoAlt={pitaLogo?.alt}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div id="share" className="mb-6 scroll-mt-24">
          <PitaShareCardBlock />
        </div>
        <div className="rounded-2xl border border-[#C9A84C]/40 bg-[#C9A84C]/10 p-4 flex gap-3 mb-6" data-testid="support-pita-privacy-note">
          <ShieldCheck className="h-5 w-5 text-[#7A5C12] shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong>Private by default.</strong> Your submission starts private and unpublished. You choose how your
            name appears (or stay anonymous), and nothing is listed publicly unless you consent and the campaign
            approves it. Your email and contact details are never shown publicly.
          </p>
        </div>

        <Card>
          <CardContent className="p-5 sm:p-7 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Your name * <span className="font-normal text-slate-400">(kept private unless you choose otherwise)</span></label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} className="h-11" data-testid="support-pita-name-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Email * <span className="font-normal text-slate-400">(never shown publicly)</span></label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className="h-11" data-testid="support-pita-email-input" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">I am a…</label>
                <Select value={form.role} onValueChange={(v) => set('role', v)}>
                  <SelectTrigger className="h-11" data-testid="support-pita-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Ward</label>
                <Select value={form.ward} onValueChange={(v) => set('ward', v)}>
                  <SelectTrigger className="h-11" data-testid="support-pita-ward-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{WARDS.map((w) => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === 'community_group' && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-slate-600 mb-1">Group or organization name</label>
                  <Input value={form.organization} onChange={(e) => set('organization', e.target.value)} className="h-11" data-testid="support-pita-org-input" />
                </div>
              )}
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">If listed publicly, show me as…</label>
                <Select value={form.display_choice} onValueChange={(v) => set('display_choice', v)}>
                  <SelectTrigger className="h-11" data-testid="support-pita-display-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISPLAY_CHOICES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.display_choice === 'pseudonym' && (
                <div>
                  <label className="block text-xs font-bold text-slate-600 mb-1">Pseudonym to display</label>
                  <Input value={form.pseudonym} onChange={(e) => set('pseudonym', e.target.value)} className="h-11" data-testid="support-pita-pseudonym-input" />
                </div>
              )}
            </div>

            {isOfficial && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-sm text-slate-600 leading-relaxed" data-testid="support-pita-official-note">
                Support from Councillors, Mayors, and candidates is <strong>personal support for the PITA tool</strong>.
                It is not an endorsement by the City of Kawartha Lakes or any City office, and the campaign verifies
                official roles before anything is listed.
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">Why do you support PITA? (optional, may be shown publicly if approved)</label>
              <Textarea value={form.statement} onChange={(e) => set('statement', e.target.value)} rows={3} maxLength={500} placeholder="A sentence or two…" data-testid="support-pita-statement-input" />
            </div>

            {/* Honeypot — hidden from real users */}
            <input type="text" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <div className="space-y-3 border-t pt-4">
              <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid="support-pita-consent-label">
                <Checkbox checked={form.consent_public} onCheckedChange={(v) => set('consent_public', !!v)} className="mt-0.5" data-testid="support-pita-consent-checkbox" />
                <span>
                  <strong>Optional:</strong> I would like to appear on the public PITA supporters page, using the display
                  choice above. I understand nothing appears until the campaign reviews and approves it.
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer" data-testid="support-pita-adult-label">
                <Checkbox checked={form.is_adult} onCheckedChange={(v) => set('is_adult', !!v)} className="mt-0.5" data-testid="support-pita-adult-checkbox" />
                <span>I confirm I am 18 years of age or older. *</span>
              </label>
            </div>

            <Button onClick={submit} disabled={busy} className="w-full h-12 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="support-pita-submit-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</> : <><Send className="h-4 w-4 mr-1.5" /> Add My Support</>}
            </Button>
          </CardContent>
        </Card>

        <div className="mt-8 rounded-2xl bg-[#1a2744] p-6 sm:p-8" data-testid="support-pita-why-block">
          <HeartHandshake className="h-6 w-6 text-[#C9A84C]" />
          <h2 className="text-white font-serif font-black text-xl mt-3">What supporting PITA means</h2>
          <p className="mt-3 text-slate-300 text-sm leading-relaxed">
            PITA is the Public Issue Tracker App: residents report local issues, see them on a public map, and track
            follow-up in the open. Report it. Map it. Track it. Get answers. Your support tells Kawartha Lakes that
            practical accountability tools matter.
          </p>
          <Button asChild variant="outline" className="mt-5 h-11 px-5 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white font-bold btn-press" data-testid="support-pita-view-supporters-btn">
            <Link to="/pita-supporters">View Supporters by Ward</Link>
          </Button>
        </div>

        <p className="text-xs text-slate-400 mt-6 text-center" data-testid="support-pita-disclaimer">
          This is not an official City of Kawartha Lakes service portal.
        </p>
      </div>
    </div>
  );
}
